import React from "react";
import { useIntl } from "react-intl";
import { Box, Grid, Stack, Typography } from "@mui/material";
import {
  CustomerLogo,
  OverrideCountChip,
  SectionHeader,
} from "../components/primitives";
import {
  COUNTRY_OPTIONS,
  CUSTOMER_STATUS_OPTIONS,
  DISTRIBUTION_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  optionLabel,
} from "../constants/options";


import { getTotalOrganizationOverrideCount } from "../model/overrideModel";
import { panelSx, ui } from "../styles";

const SummaryRow = ({ label, value, valueNode }) => (
  <Box
    sx={{
      minHeight: 34,
      py: 0.55,
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(96px, .8fr) minmax(0, 1.2fr)",
        sm: "minmax(112px, .85fr) minmax(0, 1.15fr)",
      },
      columnGap: 1.2,
      alignItems: "start",
      borderBottom: `1px solid ${ui.border}`,
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    <Typography sx={{ pt: 0.05, color: ui.muted, fontSize: 10.2, lineHeight: 1.35 }}>
      {label}
    </Typography>

    {valueNode || (
      <Typography
        sx={{
          color: ui.text,
          fontSize: 10.4,
          lineHeight: 1.35,
          fontWeight: 550,
          textAlign: "right",
          overflowWrap: "anywhere",
        }}
      >
        {value || "—"}
      </Typography>
    )}
  </Box>
);

const ReviewAddressRows = ({ address, settings }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const rows = Object.entries(settings || {})
    .filter(([, setting]) => setting?.type !== "HIDDEN")
    .sort(
      ([, a], [, b]) =>
        Number(a?.displayOrder ?? 0) - Number(b?.displayOrder ?? 0)
    );

  if (!rows.length) {
    return (
      <SummaryRow
        label={t("admin.crm.customerCard.review.address")}
        value="—"
      />
    );
  }

  return rows.map(([field, setting]) => {
    const value = address?.[field] || "";

    const displayValue =
      field === "country" && value
        ? optionLabel(intl, COUNTRY_OPTIONS, value, value)
        : value;

    return (
      <SummaryRow
        key={field}
        label={setting?.label || field}
        value={displayValue}
      />
    );
  });
};

const CustomerReviewValue = ({ customerNumber, name, src }) => (
  <Stack
    direction="row"
    spacing={0.65}
    alignItems="center"
    justifyContent="flex-end"
    sx={{ minWidth: 0 }}
  >
    <CustomerLogo src={src} size={25} fallbackIconSize={13} />

    <Box sx={{ minWidth: 0, textAlign: "right" }}>
      <Typography noWrap sx={{ color: ui.text, fontSize: 10.4, lineHeight: 1.25, fontWeight: 600 }}>
        {name || "—"}
      </Typography>

      {customerNumber && (
        <Typography noWrap sx={{ mt: 0.05, color: ui.subtle, fontSize: 8.8, lineHeight: 1.2 }}>
          {customerNumber}
        </Typography>
      )}
    </Box>
  </Stack>
);

const ReviewCard = ({ children, title }) => (
  <Box
    sx={{
      ...panelSx,
      width: "100%",
      height: "100%",
      p: 1.15,
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ minHeight: 25, display: "flex", alignItems: "flex-start", mb: 0.25 }}>
      <Typography sx={{ color: ui.text, fontSize: 11.4, lineHeight: 1.35, fontWeight: 650 }}>
        {title}
      </Typography>
    </Box>

    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const renderRows = (items, draft) =>
  items.map(([label, field, format]) => (
    <SummaryRow
      key={field}
      label={label}
      value={format ? format(draft[field]) : draft[field]}
    />
  ));

const ReviewStep = ({ baseline, draft, logoSrc }) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);

  
  const totalOverrides = baseline ? getTotalOrganizationOverrideCount(draft, baseline) : 0;

  const customerRows = [
    [t("admin.crm.customerCard.review.organizationNumber"), "orgNo"],
    [t("admin.crm.customerCard.fields.vatNumber"), "vatRegNo"],
    /*[t("admin.crm.customerCard.fields.customerOwner"), "customerOwner"],
    [t("admin.crm.customerCard.fields.status"), "customerStatus", () => statusLabel],
    [t("admin.crm.customerCard.fields.activeFrom"), "activeFrom"],*/
  ];

  const billingRows = [
    [
      t("admin.crm.customerCard.fields.paymentMethod"),
      "paymentMethod",
      (value) => optionLabel(intl, PAYMENT_METHOD_OPTIONS, value, value),
    ],
    [
      t("admin.crm.customerCard.fields.paymentTermsDays"),
      "paymentTermsDays",
      (value) => t("admin.crm.customerCard.review.days", { count: Number(value) || 0 }),
    ],
    [t("admin.crm.customerCard.fields.currency"), "currencyCode"],
    [
      t("admin.crm.customerCard.billing.distribution"),
      "distributionType",
      (value) => optionLabel(intl, DISTRIBUTION_OPTIONS, value, value),
    ],
    [t("admin.crm.customerCard.fields.invoiceEmail"), "invoiceEmail"],
  ];

  const referenceRowsLeft = [
    [t("admin.crm.customerCard.fields.yourReference"), "yourReference"],
    [t("admin.crm.customerCard.fields.ourReference"), "ourReference"],
  ];

  const referenceRowsRight = [
    [t("admin.crm.customerCard.review.poNumber"), "costplace1"],
    [t("admin.crm.customerCard.fields.projectNumber"), "costplace2"],
  ];

  return (
    <Box>
      <SectionHeader
        title={t("admin.crm.customerCard.review.title")}
        description={t("admin.crm.customerCard.review.description")}
      />

      {totalOverrides > 0 && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: -0.7, mb: 0.55 }}>
          <OverrideCountChip
            count={totalOverrides}
            label={t("admin.crm.customerCard.review.customerSpecificChanges", {
              count: totalOverrides,
            })}
          />
        </Stack>
      )}

      <Grid container spacing={1} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ReviewCard title={t("admin.crm.customerCard.review.customer")}>
            <SummaryRow
              label={t("admin.crm.customerCard.review.customer")}
              valueNode={
                <CustomerReviewValue
                  customerNumber={draft.customerNo}
                  name={draft.companyName}
                  src={logoSrc}
                />
              }
            />
            <SummaryRow
              label={t("admin.crm.customerCard.review.uniqueueLink")}
              value={
                draft.reciverCompanyId
                  ? t("admin.crm.customerCard.review.linkedId", { id: draft.reciverCompanyId })
                  : t("admin.crm.customerCard.review.notLinked")
              }
            />
            {renderRows(customerRows, draft)}
          </ReviewCard>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ReviewCard title={t("admin.crm.customerCard.billing.title")}>
            {renderRows(billingRows, draft)}
          </ReviewCard>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ReviewCard title={t("admin.crm.customerCard.addresses.billingAddress")}>
            <ReviewAddressRows
              address={draft.invoiceAddress}
              settings={draft.invoiceAddressSettings}
            />
          </ReviewCard>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <ReviewCard title={t("admin.crm.customerCard.addresses.deliveryAddress")}>
            <ReviewAddressRows
              address={
                draft.deliverySameAsBilling
                  ? draft.invoiceAddress
                  : draft.deliveryAddress
              }
              settings={
                draft.deliverySameAsBilling
                  ? draft.invoiceAddressSettings
                  : draft.deliveryAddressSettings
              }
            />
          </ReviewCard>
        </Grid>

        <Grid item xs={12} sx={{ display: "flex" }}>
          <ReviewCard title={t("admin.crm.customerCard.billing.referencesAndOrder")}>
            <Grid container columnSpacing={2}>
              <Grid item xs={12} sm={6}>
                {renderRows(referenceRowsLeft, draft)}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderRows(referenceRowsRight, draft)}
              </Grid>
            </Grid>
          </ReviewCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReviewStep;